import { line, LinePathConfig } from '@visx/shape';
import { AddSVGProps } from '@visx/shape/lib/types';
import cx from 'classnames';
import { Line as LineType } from 'd3-shape';
import { motion, MotionProps } from 'framer-motion';
import React from 'react';

type BaseProps<Datum> = {
    /** Array of data for which to generate a line shape. */
    data?: Datum[];
    /** Override render function which is passed the configured path generator as input. */
    children?: (args: { path: LineType<Datum> }) => React.ReactNode;
    /** Fill color of the path element. */
    fill?: string;
    /** className applied to path element. */
    className?: string;
    animateD?: boolean;
} & LinePathConfig<Datum>;

export type LinePathProps<Datum> = Omit<
    AddSVGProps<BaseProps<Datum>, SVGPathElement>,
    'ref'
> &
    MotionProps;

/**
 * Port of visx's LinePath component
 *
 * ref: https://github.com/airbnb/visx/blob/master/packages/visx-shape/src/shapes/LinePath.tsx
 */
export default function LinePath<Datum>({
    children,
    data = [],
    x,
    y,
    fill = 'transparent',
    className,
    curve,
    defined = () => true,
    animateD = true,
    ...restProps
}: LinePathProps<Datum>) {
    const path = line<Datum>({ x, y, defined, curve });
    if (children) return <>{children({ path })}</>;

    const d = path(data) ?? '';
    const dProps = animateD ? { animate: { d } } : { d };

    return (
        <motion.path
            className={cx('visx-linepath', className)}
            fill={fill}
            {...dProps}
            // without this a datum surrounded by nulls will not be visible
            // https://github.com/d3/d3-shape#line_defined
            strokeLinecap="round"
            {...restProps}
        />
    );
}
